/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * WindowCleaningSchool API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AnalyticsSummary,
  Appointment,
  AppointmentStatusCount,
  Course,
  CreateAppointmentBody,
  CreateCourseBody,
  CreateUserBody,
  HealthStatus,
  ListAppointmentsParams,
  LogTrafficBody,
  RevenueByMonth,
  TrafficByDay,
  TrafficLog,
  UpdateAppointmentBody,
  UpdateCourseBody,
  UpdateUserBody,
  User,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List all users
 */
export const getListUsersUrl = () => {
  return `/api/users`;
};

export const listUsers = async (options?: RequestInit): Promise<User[]> => {
  return customFetch<User[]>(getListUsersUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListUsersQueryKey = () => {
  return [`/api/users`] as const;
};

export const getListUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof listUsers>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUsersQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUsers>>> = ({
    signal,
  }) => listUsers({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUsers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListUsersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUsers>>
>;
export type ListUsersQueryError = ErrorType<unknown>;

/**
 * @summary List all users
 */

export function useListUsers<
  TData = Awaited<ReturnType<typeof listUsers>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListUsersQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create or update a user (upsert by email)
 */
export const getCreateUserUrl = () => {
  return `/api/users`;
};

export const createUser = async (
  createUserBody: CreateUserBody,
  options?: RequestInit,
): Promise<User> => {
  return customFetch<User>(getCreateUserUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createUserBody),
  });
};

export const getCreateUserMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUser>>,
    TError,
    { data: BodyType<CreateUserBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createUser>>,
  TError,
  { data: BodyType<CreateUserBody> },
  TContext
> => {
  const mutationKey = ["createUser"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createUser>>,
    { data: BodyType<CreateUserBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createUser(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof createUser>>
>;
export type CreateUserMutationBody = BodyType<CreateUserBody>;
export type CreateUserMutationError = ErrorType<unknown>;

/**
 * @summary Create or update a user (upsert by email)
 */
export const useCreateUser = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUser>>,
    TError,
    { data: BodyType<CreateUserBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createUser>>,
  TError,
  { data: BodyType<CreateUserBody> },
  TContext
> => {
  return useMutation(getCreateUserMutationOptions(options));
};

/**
 * @summary Get user by ID
 */
export const getGetUserUrl = (id: number) => {
  return `/api/users/${id}`;
};

export const getUser = async (
  id: number,
  options?: RequestInit,
): Promise<User> => {
  return customFetch<User>(getGetUserUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetUserQueryKey = (id: number) => {
  return [`/api/users/${id}`] as const;
};

export const getGetUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUser>>> = ({
    signal,
  }) => getUser(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUser>>
>;
export type GetUserQueryError = ErrorType<void>;

/**
 * @summary Get user by ID
 */

export function useGetUser<
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetUserQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a user
 */
export const getUpdateUserUrl = (id: number) => {
  return `/api/users/${id}`;
};

export const updateUser = async (
  id: number,
  updateUserBody: UpdateUserBody,
  options?: RequestInit,
): Promise<User> => {
  return customFetch<User>(getUpdateUserUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateUserBody),
  });
};

export const getUpdateUserMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUser>>,
    TError,
    { id: number; data: BodyType<UpdateUserBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateUser>>,
  TError,
  { id: number; data: BodyType<UpdateUserBody> },
  TContext
> => {
  const mutationKey = ["updateUser"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUser>>,
    { id: number; data: BodyType<UpdateUserBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateUser(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateUser>>
>;
export type UpdateUserMutationBody = BodyType<UpdateUserBody>;
export type UpdateUserMutationError = ErrorType<unknown>;

/**
 * @summary Update a user
 */
export const useUpdateUser = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUser>>,
    TError,
    { id: number; data: BodyType<UpdateUserBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateUser>>,
  TError,
  { id: number; data: BodyType<UpdateUserBody> },
  TContext
> => {
  return useMutation(getUpdateUserMutationOptions(options));
};

/**
 * @summary List all courses
 */
export const getListCoursesUrl = () => {
  return `/api/courses`;
};

export const listCourses = async (options?: RequestInit): Promise<Course[]> => {
  return customFetch<Course[]>(getListCoursesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListCoursesQueryKey = () => {
  return [`/api/courses`] as const;
};

export const getListCoursesQueryOptions = <
  TData = Awaited<ReturnType<typeof listCourses>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listCourses>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCoursesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCourses>>> = ({
    signal,
  }) => listCourses({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCourses>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCoursesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCourses>>
>;
export type ListCoursesQueryError = ErrorType<unknown>;

/**
 * @summary List all courses
 */

export function useListCourses<
  TData = Awaited<ReturnType<typeof listCourses>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listCourses>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCoursesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a course
 */
export const getCreateCourseUrl = () => {
  return `/api/courses`;
};

export const createCourse = async (
  createCourseBody: CreateCourseBody,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getCreateCourseUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createCourseBody),
  });
};

export const getCreateCourseMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCourse>>,
    TError,
    { data: BodyType<CreateCourseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCourse>>,
  TError,
  { data: BodyType<CreateCourseBody> },
  TContext
> => {
  const mutationKey = ["createCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCourse>>,
    { data: BodyType<CreateCourseBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createCourse(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCourse>>
>;
export type CreateCourseMutationBody = BodyType<CreateCourseBody>;
export type CreateCourseMutationError = ErrorType<unknown>;

/**
 * @summary Create a course
 */
export const useCreateCourse = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCourse>>,
    TError,
    { data: BodyType<CreateCourseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createCourse>>,
  TError,
  { data: BodyType<CreateCourseBody> },
  TContext
> => {
  return useMutation(getCreateCourseMutationOptions(options));
};

/**
 * @summary Get course by ID
 */
export const getGetCourseUrl = (id: number) => {
  return `/api/courses/${id}`;
};

export const getCourse = async (
  id: number,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getGetCourseUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetCourseQueryKey = (id: number) => {
  return [`/api/courses/${id}`] as const;
};

export const getGetCourseQueryOptions = <
  TData = Awaited<ReturnType<typeof getCourse>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCourse>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCourseQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourse>>> = ({
    signal,
  }) => getCourse(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetCourseQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCourse>>
>;
export type GetCourseQueryError = ErrorType<unknown>;

/**
 * @summary Get course by ID
 */

export function useGetCourse<
  TData = Awaited<ReturnType<typeof getCourse>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCourse>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCourseQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a course
 */
export const getUpdateCourseUrl = (id: number) => {
  return `/api/courses/${id}`;
};

export const updateCourse = async (
  id: number,
  updateCourseBody: UpdateCourseBody,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getUpdateCourseUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateCourseBody),
  });
};

export const getUpdateCourseMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateCourse>>,
    TError,
    { id: number; data: BodyType<UpdateCourseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateCourse>>,
  TError,
  { id: number; data: BodyType<UpdateCourseBody> },
  TContext
> => {
  const mutationKey = ["updateCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateCourse>>,
    { id: number; data: BodyType<UpdateCourseBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateCourse(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateCourse>>
>;
export type UpdateCourseMutationBody = BodyType<UpdateCourseBody>;
export type UpdateCourseMutationError = ErrorType<unknown>;

/**
 * @summary Update a course
 */
export const useUpdateCourse = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateCourse>>,
    TError,
    { id: number; data: BodyType<UpdateCourseBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateCourse>>,
  TError,
  { id: number; data: BodyType<UpdateCourseBody> },
  TContext
> => {
  return useMutation(getUpdateCourseMutationOptions(options));
};

/**
 * @summary Delete a course
 */
export const getDeleteCourseUrl = (id: number) => {
  return `/api/courses/${id}`;
};

export const deleteCourse = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteCourseUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteCourseMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCourse>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCourse>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCourse>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteCourse(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCourse>>
>;

export type DeleteCourseMutationError = ErrorType<unknown>;

/**
 * @summary Delete a course
 */
export const useDeleteCourse = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCourse>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteCourse>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteCourseMutationOptions(options));
};

/**
 * @summary List appointments
 */
export const getListAppointmentsUrl = (params?: ListAppointmentsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/appointments?${stringifiedParams}`
    : `/api/appointments`;
};

export const listAppointments = async (
  params?: ListAppointmentsParams,
  options?: RequestInit,
): Promise<Appointment[]> => {
  return customFetch<Appointment[]>(getListAppointmentsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListAppointmentsQueryKey = (
  params?: ListAppointmentsParams,
) => {
  return [`/api/appointments`, ...(params ? [params] : [])] as const;
};

export const getListAppointmentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAppointments>>,
  TError = ErrorType<unknown>,
>(
  params?: ListAppointmentsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAppointments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAppointmentsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAppointments>>
  > = ({ signal }) => listAppointments(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAppointments>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAppointmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAppointments>>
>;
export type ListAppointmentsQueryError = ErrorType<unknown>;

/**
 * @summary List appointments
 */

export function useListAppointments<
  TData = Awaited<ReturnType<typeof listAppointments>>,
  TError = ErrorType<unknown>,
>(
  params?: ListAppointmentsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAppointments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAppointmentsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Book an appointment
 */
export const getCreateAppointmentUrl = () => {
  return `/api/appointments`;
};

export const createAppointment = async (
  createAppointmentBody: CreateAppointmentBody,
  options?: RequestInit,
): Promise<Appointment> => {
  return customFetch<Appointment>(getCreateAppointmentUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createAppointmentBody),
  });
};

export const getCreateAppointmentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAppointment>>,
    TError,
    { data: BodyType<CreateAppointmentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAppointment>>,
  TError,
  { data: BodyType<CreateAppointmentBody> },
  TContext
> => {
  const mutationKey = ["createAppointment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAppointment>>,
    { data: BodyType<CreateAppointmentBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createAppointment(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAppointmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAppointment>>
>;
export type CreateAppointmentMutationBody = BodyType<CreateAppointmentBody>;
export type CreateAppointmentMutationError = ErrorType<unknown>;

/**
 * @summary Book an appointment
 */
export const useCreateAppointment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAppointment>>,
    TError,
    { data: BodyType<CreateAppointmentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createAppointment>>,
  TError,
  { data: BodyType<CreateAppointmentBody> },
  TContext
> => {
  return useMutation(getCreateAppointmentMutationOptions(options));
};

/**
 * @summary Get appointment by ID
 */
export const getGetAppointmentUrl = (id: number) => {
  return `/api/appointments/${id}`;
};

export const getAppointment = async (
  id: number,
  options?: RequestInit,
): Promise<Appointment> => {
  return customFetch<Appointment>(getGetAppointmentUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetAppointmentQueryKey = (id: number) => {
  return [`/api/appointments/${id}`] as const;
};

export const getGetAppointmentQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppointment>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAppointment>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAppointmentQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAppointment>>> = ({
    signal,
  }) => getAppointment(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAppointment>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAppointmentQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAppointment>>
>;
export type GetAppointmentQueryError = ErrorType<unknown>;

/**
 * @summary Get appointment by ID
 */

export function useGetAppointment<
  TData = Awaited<ReturnType<typeof getAppointment>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAppointment>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAppointmentQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update appointment status or details
 */
export const getUpdateAppointmentUrl = (id: number) => {
  return `/api/appointments/${id}`;
};

export const updateAppointment = async (
  id: number,
  updateAppointmentBody: UpdateAppointmentBody,
  options?: RequestInit,
): Promise<Appointment> => {
  return customFetch<Appointment>(getUpdateAppointmentUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateAppointmentBody),
  });
};

export const getUpdateAppointmentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAppointment>>,
    TError,
    { id: number; data: BodyType<UpdateAppointmentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAppointment>>,
  TError,
  { id: number; data: BodyType<UpdateAppointmentBody> },
  TContext
> => {
  const mutationKey = ["updateAppointment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAppointment>>,
    { id: number; data: BodyType<UpdateAppointmentBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAppointment(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAppointmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAppointment>>
>;
export type UpdateAppointmentMutationBody = BodyType<UpdateAppointmentBody>;
export type UpdateAppointmentMutationError = ErrorType<unknown>;

/**
 * @summary Update appointment status or details
 */
export const useUpdateAppointment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAppointment>>,
    TError,
    { id: number; data: BodyType<UpdateAppointmentBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateAppointment>>,
  TError,
  { id: number; data: BodyType<UpdateAppointmentBody> },
  TContext
> => {
  return useMutation(getUpdateAppointmentMutationOptions(options));
};

/**
 * @summary Cancel/delete appointment
 */
export const getDeleteAppointmentUrl = (id: number) => {
  return `/api/appointments/${id}`;
};

export const deleteAppointment = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteAppointmentUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteAppointmentMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAppointment>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAppointment>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteAppointment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAppointment>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteAppointment(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAppointmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAppointment>>
>;

export type DeleteAppointmentMutationError = ErrorType<unknown>;

/**
 * @summary Cancel/delete appointment
 */
export const useDeleteAppointment = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAppointment>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteAppointment>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteAppointmentMutationOptions(options));
};

/**
 * @summary Log a page view
 */
export const getLogTrafficUrl = () => {
  return `/api/traffic`;
};

export const logTraffic = async (
  logTrafficBody: LogTrafficBody,
  options?: RequestInit,
): Promise<TrafficLog> => {
  return customFetch<TrafficLog>(getLogTrafficUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(logTrafficBody),
  });
};

export const getLogTrafficMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logTraffic>>,
    TError,
    { data: BodyType<LogTrafficBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logTraffic>>,
  TError,
  { data: BodyType<LogTrafficBody> },
  TContext
> => {
  const mutationKey = ["logTraffic"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logTraffic>>,
    { data: BodyType<LogTrafficBody> }
  > = (props) => {
    const { data } = props ?? {};

    return logTraffic(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LogTrafficMutationResult = NonNullable<
  Awaited<ReturnType<typeof logTraffic>>
>;
export type LogTrafficMutationBody = BodyType<LogTrafficBody>;
export type LogTrafficMutationError = ErrorType<unknown>;

/**
 * @summary Log a page view
 */
export const useLogTraffic = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logTraffic>>,
    TError,
    { data: BodyType<LogTrafficBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof logTraffic>>,
  TError,
  { data: BodyType<LogTrafficBody> },
  TContext
> => {
  return useMutation(getLogTrafficMutationOptions(options));
};

/**
 * @summary Get analytics summary (admin dashboard stats)
 */
export const getGetAnalyticsSummaryUrl = () => {
  return `/api/analytics/summary`;
};

export const getAnalyticsSummary = async (
  options?: RequestInit,
): Promise<AnalyticsSummary> => {
  return customFetch<AnalyticsSummary>(getGetAnalyticsSummaryUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAnalyticsSummaryQueryKey = () => {
  return [`/api/analytics/summary`] as const;
};

export const getGetAnalyticsSummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAnalyticsSummary>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAnalyticsSummaryQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAnalyticsSummary>>
  > = ({ signal }) => getAnalyticsSummary({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAnalyticsSummary>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAnalyticsSummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalyticsSummary>>
>;
export type GetAnalyticsSummaryQueryError = ErrorType<unknown>;

/**
 * @summary Get analytics summary (admin dashboard stats)
 */

export function useGetAnalyticsSummary<
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAnalyticsSummary>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAnalyticsSummaryQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Page views grouped by day (last 30 days)
 */
export const getGetTrafficByDayUrl = () => {
  return `/api/analytics/traffic-by-day`;
};

export const getTrafficByDay = async (
  options?: RequestInit,
): Promise<TrafficByDay[]> => {
  return customFetch<TrafficByDay[]>(getGetTrafficByDayUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetTrafficByDayQueryKey = () => {
  return [`/api/analytics/traffic-by-day`] as const;
};

export const getGetTrafficByDayQueryOptions = <
  TData = Awaited<ReturnType<typeof getTrafficByDay>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTrafficByDay>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTrafficByDayQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTrafficByDay>>> = ({
    signal,
  }) => getTrafficByDay({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTrafficByDay>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTrafficByDayQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTrafficByDay>>
>;
export type GetTrafficByDayQueryError = ErrorType<unknown>;

/**
 * @summary Page views grouped by day (last 30 days)
 */

export function useGetTrafficByDay<
  TData = Awaited<ReturnType<typeof getTrafficByDay>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTrafficByDay>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTrafficByDayQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Appointment counts by status
 */
export const getGetAppointmentsByStatusUrl = () => {
  return `/api/analytics/appointments-by-status`;
};

export const getAppointmentsByStatus = async (
  options?: RequestInit,
): Promise<AppointmentStatusCount[]> => {
  return customFetch<AppointmentStatusCount[]>(
    getGetAppointmentsByStatusUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetAppointmentsByStatusQueryKey = () => {
  return [`/api/analytics/appointments-by-status`] as const;
};

export const getGetAppointmentsByStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppointmentsByStatus>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppointmentsByStatus>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAppointmentsByStatusQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAppointmentsByStatus>>
  > = ({ signal }) => getAppointmentsByStatus({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAppointmentsByStatus>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAppointmentsByStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAppointmentsByStatus>>
>;
export type GetAppointmentsByStatusQueryError = ErrorType<unknown>;

/**
 * @summary Appointment counts by status
 */

export function useGetAppointmentsByStatus<
  TData = Awaited<ReturnType<typeof getAppointmentsByStatus>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppointmentsByStatus>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAppointmentsByStatusQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Revenue stats by month
 */
export const getGetRevenueStatsUrl = () => {
  return `/api/analytics/revenue`;
};

export const getRevenueStats = async (
  options?: RequestInit,
): Promise<RevenueByMonth[]> => {
  return customFetch<RevenueByMonth[]>(getGetRevenueStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetRevenueStatsQueryKey = () => {
  return [`/api/analytics/revenue`] as const;
};

export const getGetRevenueStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getRevenueStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getRevenueStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRevenueStatsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRevenueStats>>> = ({
    signal,
  }) => getRevenueStats({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRevenueStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetRevenueStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRevenueStats>>
>;
export type GetRevenueStatsQueryError = ErrorType<unknown>;

/**
 * @summary Revenue stats by month
 */

export function useGetRevenueStats<
  TData = Awaited<ReturnType<typeof getRevenueStats>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getRevenueStats>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetRevenueStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
